<template>
  <div>
    <b-form
      inline
      class="mb-2"
    >
      <b-button
        v-ripple.400="'rgba(255, 255, 255, 0.15)'"
        variant="primary"
        class="d-inline-block mr-1 mb-1"
        @click="createNew"
      >
        {{ $t('Add') }}
      </b-button>

      <b-form-input
        v-model="searchTerm"
        :placeholder=" $t('message.SearchPlaceholder')"
        type="text"
        class="d-inline mb-1"
      />

      <b-dropdown
        v-if="selectedRecords.length>0"
        id="dropdown-actions"
        v-ripple.400="'rgba(255, 255, 255, 0.15)'"
        :text="$t('Actions')"
        variant="primary"
        class="ml-1 mb-1"
      >
        <b-dropdown-item
          variant="success"
          @click="setStatusSelectedRecords(true)"
        >
          <feather-icon
            icon="EyeIcon"
          />
          {{ $t('Set as Active') }}
        </b-dropdown-item>
        <b-dropdown-item
          variant="warning"
          @click="setStatusSelectedRecords(false)"
        >
          <feather-icon
            icon="EyeOffIcon"
          />
          {{ $t('Set as Passive') }}
        </b-dropdown-item>
        <!-- clone page -->
        <b-dropdown-item
          variant="info"
          @click="cloneSelectedRecords(false)"
        >
          <feather-icon
            icon="CopyIcon"
          />
          {{ $t('Clone') }}
        </b-dropdown-item>
        <!-- clone page with filter -->
        <b-dropdown-item
          variant="info"
          @click="cloneSelectedRecords(true)"
        >
          <feather-icon
            icon="CopyIcon"
          />
          {{ $t('Clone + Filter') }}
        </b-dropdown-item>

        <!-- AI active -->
        <template>
          <!-- AI active -->
          <b-dropdown-item
            variant="success"
            @click="setAIStatusSelectedRecords(true)"
          >
            <feather-icon
              icon="ZapIcon"
            />
            {{ $t('AI Active') }}
          </b-dropdown-item>
          <!-- AI passive -->
          <b-dropdown-item
            variant="warning"
            @click="setAIStatusSelectedRecords(false)"
          >
            <feather-icon
              icon="ZapOffIcon"
            />
            {{ $t('AI Passive') }}
          </b-dropdown-item>
        </template>

        <b-dropdown-divider />
        <b-dropdown-item
          variant="danger"
          @click="confirmDelete"
        >
          <feather-icon
            icon="Trash2Icon"
          />
          {{ $t('Delete') }}
        </b-dropdown-item>
      </b-dropdown>

    </b-form>

    <!-- table -->
    <vue-good-table
      style-class="vgt-table striped"
      :columns="columns"
      :rows="data"
      :sort-options="{
        enabled: true,
        initialSortBy: {field: 'id', type: 'desc'}
      }"
      :search-options="{
        enabled: true,
        externalQuery: searchTerm }"
      :select-options="{
        enabled: true,
        selectOnCheckboxOnly: true, // only select when checkbox is clicked instead of the row
        selectionInfoClass: 'custom-class',
        selectionText: 'rows selected',
        clearSelectionText: 'clear',
        disableSelectInfo: true, // disable the select info panel on top
      }"
      :pagination-options="{
        enabled: true,
        perPage:pageLength,
        mode: 'records',
        perPage: 10,
        perPageDropdown: [10, 50, 100],
        dropdownAllowAll: false,
      }"
      @on-selected-rows-change="selectedRowsChanged"
    >

      <template
        slot="table-column"
        slot-scope="props"
      >
        <span>
          {{ $t(props.column.label) }}
        </span>
      </template>

      <template
        slot="table-row"
        slot-scope="props"
      >

        <!-- Column: id -->
        <span
          v-if="props.column.field === 'id'"
          class="text-nowrap"
        >
          <b-link @click="edit(props.row)">
            <feather-icon
              icon="EditIcon"
            />

          </b-link>
        </span>
        <span
          v-else-if="props.column.field === 'icon'"
          class="text-nowrap"
        >
          <feather-icon
            :icon="props.row[props.column.field]||'CircleIcon'"
          />
        </span>
        <span
          v-else-if="props.column.field === 'userGroupNames'"
          class="text-nowrap"
        >
          <b-badge
            v-for="(group, index) in props.row.userGroupNames"
            :key="index"
            pill
            variant="success"
            class="text-capitalize mr-1"
          >
            {{ group }}
          </b-badge>
        </span>
        <span
          v-else-if="['aiActive', 'status'].includes(props.column.field)"
          class="text-nowrap"
        >
          <b-badge
            pill
            :variant="`light-${resolveStatus(props.row[props.column.field])}`"
            class="text-capitalize"
          >
            {{ props.row[props.column.field] }}
          </b-badge>
        </span>

        <!-- Column: Common -->
        <span v-else>
          {{ props.formattedRow[props.column.field] }}
        </span>
      </template>

    </vue-good-table>

    <modal-item
      item-type="page"
      :edit-item="edit_item"
      @submit="getPages"
    />

    <b-modal
      id="filter-clone-modal"
      :title="$t('Clone With Filter')"
      :ok-title="$t('Clone')"
      :cancel-title="$t('Cancel')"
      cancel-variant="outline-secondary"
      size="lg"
      :busy="cloneInProgress"
      :no-close-on-backdrop="true"
      @show="resetCloneFilter"
      @ok="cloneSelectedRecordsWithFilter"
    >
      <b-form-group
        :label="$t('Project')"
        label-for="clone-filter-project"
      >
        <v-select
          id="clone-filter-project"
          v-model="cloneFilter.projectId"
          :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
          :options="optionProject"
          :reduce="project => project.id"
          label="projectName"
          :placeholder="$t('Select project...')"
          @input="cloneFilter.filterConfig.filters = []"
        />
      </b-form-group>

      <form-filter
        v-if="cloneFilter.projectId"
        v-model="cloneFilter.filterConfig.filters"
        :project-id="cloneFilter.projectId"
        :forfilter="true"
      />
    </b-modal>

  </div>
</template>

<script>
import useJwt from '@/auth/jwt/useJwt'

import {
  BLink, BFormInput, BForm, BButton, BDropdown, BDropdownItem, BDropdownDivider, BBadge, BModal, BFormGroup,
} from 'bootstrap-vue'
import Ripple from 'vue-ripple-directive'
import { VueGoodTable } from 'vue-good-table'
import vSelect from 'vue-select'
import { checkFilterSyntax } from '@core/layouts/utils'
import ModalItem from './ModalItem.vue'
import FormFilter from './forms/FormFilter.vue'

export default {
  components: {
    VueGoodTable,
    BLink,
    BDropdown,
    BDropdownItem,
    BDropdownDivider,
    BFormInput,
    BForm,
    BButton,
    BBadge,
    BModal,
    BFormGroup,
    vSelect,
    ModalItem,
    FormFilter,
  },
  directives: {
    Ripple,
  },
  data() {
    return {
      data: [],
      columns: [
        {
          label: '#',
          field: 'id',
          type: 'number',
          tdClass: 'text-center',
          thClass: 'text-center',
          width: '50px',
        },
        {
          label: '',
          field: 'icon',
          tdClass: 'text-center',
          thClass: 'text-center',
          width: '50px',
          sortable: false,
        },
        {
          label: 'Title',
          field: 'title',
        },
        {
          label: 'Parent Title',
          field: 'parentTitle',
        },
        {
          label: 'Page Order',
          field: 'orderNumber',
          type: 'number',
          tdClass: 'text-center',
          thClass: 'text-center',
        },
        {
          label: 'User Groups',
          field: 'userGroupNames',
        },
        {
          label: 'Status',
          field: 'status',
        },
        {
          label: 'AI Status',
          field: 'aiActive',
        },
        {
          label: 'Box Count',
          field: 'boxCount',
          type: 'number',
          tdClass: 'text-center',
          thClass: 'text-center',
        },
      ],
      pageLength: 10,
      searchTerm: '',
      selectedRecords: [],
      edit_item: null,
      optionProject: [],
      cloneInProgress: false,
      cloneFilter: {
        projectId: null,
        filterConfig: { filters: [] },
      },
    }
  },
  created() {
    this.getPages(false)
    this.getProjectOptions()
  },
  methods: {
    resolveStatus(status) {
      if (status) return 'success'
      if (!status) return 'danger'
      return 'secondary'
    },
    getPages(sendRefreshSignal = true) {
      this.edit_item = null
      this.$bvModal.hide('add-item')
      useJwt.axiosIns.get('page')
        .then(response => {
          if (response.data) {
            this.data = response.data
            if (sendRefreshSignal) {
              this.$root.$emit('refreshMenu')
            }
          }
        })
        .catch(e => {
          console.log(e)
        })
    },
    getProjectOptions() {
      useJwt.axiosIns.get('project').then(response => {
        this.optionProject = response.data
      })
    },
    edit(item) {
      this.edit_item = JSON.parse(JSON.stringify(item))
      this.edit_item.pdfAttachments = JSON.parse(this.edit_item.pdfAttachments)
      this.$bvModal.show('add-item')
    },
    createNew() {
      this.$bvModal.show('add-item')
    },
    selectedRowsChanged(vals) {
      this.selectedRecords = vals.selectedRows.map(x => x.id)
    },

    confirmDelete() {
      this.$swal({
        title: 'Are you sure?',
        text: `You are about to delete ${this.selectedRecords.length} record(s)...`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Yes, delete it!',
        customClass: {
          confirmButton: 'btn btn-primary',
          cancelButton: 'btn btn-outline-danger ml-1',
        },
        buttonsStyling: false,
      }).then(result => {
        if (result.value) {
          this.deleteSelectedRecords()
        }
      })
    },

    deleteSelectedRecords() {
      const promises = this.selectedRecords.map(x => useJwt.axiosIns.delete(
        `page/${x}`,
      ))

      Promise.all(promises).then(() => {
        this.getPages(true)
        // show message
        this.$swal({
          icon: 'success',
          title: 'Deleted!',
          text: `${this.selectedRecords.length} record(s) has been deleted.`,
          customClass: {
            confirmButton: 'btn btn-success',
          },
        })
      })
    },
    cloneSelectedRecords(withFilter = false) {
      if (withFilter) {
        this.$bvModal.show('filter-clone-modal')
        return
      }
      const promises = this.selectedRecords.map(x => useJwt.axiosIns.get(
        `page/${x}/clone`,
      ))

      Promise.all(promises).then(() => {
        this.getPages(true)
        // show message
        this.$swal({
          icon: 'success',
          title: this.$t('Cloned!'),
          text: `${this.selectedRecords.length} ${this.$t('page(s) has been cloned')}.`,
          customClass: {
            confirmButton: 'btn btn-success',
          },
        })
      })
    },
    resetCloneFilter() {
      this.cloneInProgress = false
      this.cloneFilter = {
        projectId: null,
        filterConfig: { filters: [] },
      }
    },
    async cloneSelectedRecordsWithFilter(event) {
      event.preventDefault()
      const { filters } = this.cloneFilter.filterConfig
      if (!this.cloneFilter.projectId || !filters.length) {
        this.$swal({
          icon: 'warning',
          title: this.$t('Missing information'),
          text: this.$t('Please select a project and add a filter.'),
          customClass: {
            confirmButton: 'btn btn-primary',
          },
        })
        return
      }

      this.cloneInProgress = true
      try {
        const syntaxResponse = await checkFilterSyntax(this.cloneFilter.projectId, filters)
        if (!syntaxResponse.data) {
          this.$swal({
            icon: 'warning',
            title: this.$t('Invalid Filter!'),
            text: this.$t('Please use operators to join filters...'),
            customClass: {
              confirmButton: 'btn btn-primary',
            },
          })
          return
        }

        const cloneConfig = {
          projectId: this.cloneFilter.projectId,
          filterConfigJson: JSON.stringify(this.cloneFilter.filterConfig),
        }
        await Promise.all(this.selectedRecords.map(pageId => useJwt.axiosIns.post(
          `page/${pageId}/clonewithfilter`, cloneConfig,
        )))
        this.$bvModal.hide('filter-clone-modal')
        this.getPages(true)
        this.$swal({
          icon: 'success',
          title: this.$t('Cloned!'),
          text: `${this.selectedRecords.length} ${this.$t('page(s) has been cloned')}.`,
          customClass: {
            confirmButton: 'btn btn-success',
          },
        })
      } catch (error) {
        console.log(error)
      } finally {
        this.cloneInProgress = false
      }
    },
    setStatusSelectedRecords(status) {
      const promises = this.selectedRecords.map(x => useJwt.axiosIns.patch(
        `page/${x}/setStatus/${status}`,
      ))

      Promise.all(promises).then(() => {
        this.getPages(true)
        // show message
        this.$swal({
          icon: 'success',
          title: 'Success!',
          text: `${this.selectedRecords.length} record(s) has been set status:${status}.`,
          customClass: {
            confirmButton: 'btn btn-success',
          },
        })
      })
    },
    setAIStatusSelectedRecords(status) {
      const promises = this.selectedRecords.map(x => useJwt.axiosIns.patch(
        `page/${x}/setAIStatus/${status}`,
      ))

      Promise.all(promises).then(() => {
        this.getPages(true)
        // show message
        this.$swal({
          icon: 'success',
          title: 'Success!',
          text: `${this.selectedRecords.length} record(s) has been set status:${status}.`,
          customClass: {
            confirmButton: 'btn btn-success',
          },
        })
      })
    },
  },
}
</script>

<style lang="scss" >
@import '@core/scss/vue/libs/vue-good-table.scss';
</style>
